import { useRef, useState } from "react";
import { toast } from "sonner";

import {
  useApproveCarrierRefundLine,
  useCarrierShipment,
  useDisputeCarrierRefundLine,
} from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDate, formatMoney } from "@/lib/format";

export interface RefundLineApproval {
  refundRequestId: string;
  lineId: string;
  shipmentId: string | null;
  shipmentLabel: string;
  chargeType: string;
  amountNetCents: number;
  taxCents: number;
  amountGrossCents: number;
  createdAt: string;
  requestedAt?: string;
  source: string;
  reason: string | null;
  requester?: {
    type: "Customer" | "EZX staff" | "Automated system";
    name: string | null;
    email: string | null;
    phone: string | null;
    customerNumber: number | null;
  } | null;
  customer?: {
    name: string;
    email: string;
    phone: string | null;
    customerNumber: number;
  } | null;
}

function contact(email: string | null, phone: string | null): string {
  return [email, phone].filter(Boolean).join(" · ") || "-";
}

export function ApproveRefundLineSheet({
  approval,
  onOpenChange,
}: {
  approval: RefundLineApproval | null;
  onOpenChange: (open: boolean) => void;
}) {
  const approve = useApproveCarrierRefundLine();
  const dispute = useDisputeCarrierRefundLine();
  const shipment = useCarrierShipment(approval?.shipmentId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"decision" | "dispute">("decision");
  const [disputeReason, setDisputeReason] = useState("");
  const lock = useRef(false);
  const shipmentCustomer = shipment.data?.order?.placerCustomer;
  const customer = approval?.customer
    ? approval.customer
    : shipmentCustomer
      ? {
          name:
            [shipmentCustomer.firstName, shipmentCustomer.lastName]
              .filter(Boolean)
              .join(" ") || shipmentCustomer.email,
          email: shipmentCustomer.email,
          phone: shipmentCustomer.phone,
          customerNumber: shipmentCustomer.customerNumber,
        }
      : null;

  const submitApproval = () => {
    if (!approval || lock.current) return;
    lock.current = true;
    setSaving(true);
    setError(null);
    approve.mutate(
      {
        refundRequestId: approval.refundRequestId,
        lineId: approval.lineId,
      },
      {
        onSuccess: () => {
          toast.success(
            "Provider component authorized — customer refund may still be pending.",
            {
              description:
                "EZX completes customer settlement after all refund components are authorized.",
            }
          );
          onOpenChange(false);
        },
        onError: (caught: unknown) => {
          const message = hasHttpStatus(caught, 403)
            ? "You do not have permission to authorize this component."
            : hasHttpStatus(caught, 409)
              ? "This request changed. Refresh and review its current state."
              : "The provider component could not be authorized. Please retry.";
          setError(message);
          toast.error(message);
        },
        onSettled: () => {
          lock.current = false;
          setSaving(false);
        },
      }
    );
  };

  const submitDispute = () => {
    if (!approval || lock.current) return;
    const reason = disputeReason.trim();
    if (!reason) {
      setError("A dispute reason is required.");
      return;
    }
    lock.current = true;
    setSaving(true);
    setError(null);
    dispute.mutate(
      {
        refundRequestId: approval.refundRequestId,
        lineId: approval.lineId,
        reason,
      },
      {
        onSuccess: () => {
          toast.success("Refund component sent to EZX review.");
          onOpenChange(false);
        },
        onError: (caught: unknown) => {
          const message = hasHttpStatus(caught, 403)
            ? "You do not have permission to dispute this component."
            : hasHttpStatus(caught, 409)
              ? "This request changed. Refresh and review its current state."
              : "The dispute could not be submitted. Please retry.";
          setError(message);
          toast.error(message);
        },
        onSettled: () => {
          lock.current = false;
          setSaving(false);
        },
      }
    );
  };

  return (
    <DetailDrawer
      open={approval !== null}
      onOpenChange={(open) => {
        if (!open && !saving) {
          setError(null);
          setMode("decision");
          setDisputeReason("");
          onOpenChange(false);
        }
      }}
      title={
        approval
          ? `Authorize provider component · ${approval.shipmentLabel}`
          : ""
      }
      description="Review only your carrier-owned component. EZX performs customer settlement."
      widthClassName="sm:max-w-md"
      closeDisabled={saving}
      footer={
        approval ? (
          mode === "dispute" ? (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                size="touch"
                disabled={saving}
                onClick={() => {
                  setError(null);
                  setMode("decision");
                }}
              >
                Back
              </Button>
              <Button
                type="button"
                size="touch"
                disabled={saving}
                onClick={submitDispute}
              >
                {saving ? "Submitting…" : "Submit dispute"}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                size="touch"
                disabled={saving}
                onClick={() => {
                  setError(null);
                  setMode("dispute");
                }}
              >
                Dispute / request EZX review
              </Button>
              <Button
                type="button"
                size="touch"
                disabled={saving}
                onClick={submitApproval}
              >
                {saving ? "Authorizing…" : "Authorize provider component"}
              </Button>
            </div>
          )
        ) : undefined
      }
    >
      {approval && (
        <div className="space-y-4">
          <dl className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Request ID</dt>
            <dd className="break-all font-mono text-xs">
              {approval.refundRequestId}
            </dd>
            <dt className="text-muted-foreground">Line ID</dt>
            <dd className="break-all font-mono text-xs">{approval.lineId}</dd>
            <dt className="text-muted-foreground">Shipment</dt>
            <dd className="font-mono text-xs">{approval.shipmentLabel}</dd>
            <dt className="text-muted-foreground">Order</dt>
            <dd className="font-mono text-xs">
              {shipment.data?.order
                ? `ORD-${shipment.data.order.orderNumber}`
                : "-"}
            </dd>
            <dt className="text-muted-foreground">Customer</dt>
            <dd>
              {customer
                ? `${customer.name} · Customer #${customer.customerNumber}`
                : "-"}
            </dd>
            <dt className="text-muted-foreground">Customer contact</dt>
            <dd className="break-words">
              {customer ? contact(customer.email, customer.phone) : "-"}
            </dd>
            <dt className="text-muted-foreground">Requested by</dt>
            <dd className="break-words">
              {approval.requester
                ? approval.requester.name
                  ? `${approval.requester.type} · ${approval.requester.name}`
                  : approval.requester.type
                : "Unavailable on this approval record"}
            </dd>
            <dt className="text-muted-foreground">Requester contact</dt>
            <dd className="break-words">
              {approval.requester
                ? contact(approval.requester.email, approval.requester.phone)
                : "-"}
            </dd>
            <dt className="text-muted-foreground">Charge</dt>
            <dd className="break-words">
              {approval.chargeType.replace(/_/g, " ").toLowerCase()}
            </dd>
            <dt className="text-muted-foreground">Net</dt>
            <dd>{formatMoney(approval.amountNetCents)}</dd>
            <dt className="text-muted-foreground">Tax</dt>
            <dd>{formatMoney(approval.taxCents)}</dd>
            <dt className="font-medium">Refund total</dt>
            <dd className="font-semibold">
              {formatMoney(approval.amountGrossCents)} CAD
            </dd>
            <dt className="text-muted-foreground">Source</dt>
            <dd className="break-words">
              {approval.source.replace(/_/g, " ")}
            </dd>
            <dt className="text-muted-foreground">Reason</dt>
            <dd className="break-words">{approval.reason ?? "-"}</dd>
            <dt className="text-muted-foreground">Requested</dt>
            <dd>{formatDate(approval.requestedAt ?? approval.createdAt)}</dd>
          </dl>
          {shipment.isLoading && (
            <p role="status" className="text-sm text-muted-foreground">
              Loading Shipment context…
            </p>
          )}
          {shipment.error && (
            <p role="alert" className="text-sm text-amber-800">
              Shipment customer context could not be loaded. Refund details
              remain available.
            </p>
          )}
          <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p>
              Authorization confirms your carrier-owned component. EZX performs
              the customer settlement.
            </p>
            <p>
              When EZX settles this funding group, the approved amount is
              recorded as a carrier payable adjustment. This funding group may
              settle independently while other components remain under review.
            </p>
          </div>
          {mode === "dispute" && (
            <div className="space-y-2">
              <label
                htmlFor="refund-dispute-reason"
                className="text-sm font-medium"
              >
                Dispute reason
              </label>
              <Textarea
                id="refund-dispute-reason"
                value={disputeReason}
                disabled={saving}
                placeholder="Explain why EZX should review this component"
                onChange={(event) => setDisputeReason(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This escalates the component to EZX. It does not deny or settle
                the customer refund.
              </p>
            </div>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          {saving && (
            <p role="status" className="text-sm text-muted-foreground">
              {mode === "dispute"
                ? "Submitting dispute…"
                : "Authorizing provider component…"}
            </p>
          )}
        </div>
      )}
    </DetailDrawer>
  );
}
